import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ContactService } from '../../../../../services/contact.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { DialogService } from '../../../../../services/dialog.service';
import { IError } from '../../../../../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { BaseService } from '../../../../../services/base.service';
import * as _ from 'lodash';
import { trimValidator } from '../../../../../helpers';

interface IContactForm {
  name: FormControl;
  phone: FormControl;
  email: FormControl;
  address: FormControl;
  comment: FormControl;
}

@Component({
  selector: 'app-contact-detail',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ContactsComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  public contactForm!: FormGroup<IContactForm>;
  public contact!: any;
  public contact_id!: string;

  public isValidationInvalid: boolean = false;
  public hasChange: boolean = false;
  public isLoader: boolean = false;

  constructor(
    private contactService: ContactService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    public baseService: BaseService,
  ) {}

  public ngOnInit(): void {
    this.contact_id = this.payload?.contact_id;
    this.isLoader = true;
    this.initContact();
  }

  onCreateGroupFormValueChange() {
    const initialValue: any = { ...this.contactForm.getRawValue() };
    this.contactForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.hasChange = !_.isEqual(value, initialValue);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initForm(): void {
    if (!this.contact) {
      this.contactForm = new FormGroup<IContactForm>({
        name: new FormControl(null, [Validators.required, trimValidator]),
        phone: new FormControl(null),
        email: new FormControl(null, [Validators.email]),
        address: new FormControl(null),
        comment: new FormControl(null, [Validators.maxLength(5000)]),
      });
    } else {
      this.contactForm = new FormGroup<IContactForm>({
        name: new FormControl(this.contact.name),
        phone: new FormControl(this.contact?.phones && this.contact?.phones[0]),
        email: new FormControl(this.contact?.emails && this.contact?.emails[0], Validators.email),
        address: new FormControl(this.contact?.addresses && this.contact?.addresses[0]),
        comment: new FormControl(this.contact?.comment, [Validators.maxLength(5000), trimValidator]),
      });
      this.onCreateGroupFormValueChange();
    }
  }

  public onSaveContact(): void {
    const { name, phone, email, address, comment } = this.contactForm.getRawValue();

    this.isValidationInvalid = false;

    if (this.contactForm.controls.name.invalid) {
      this.contactForm.controls.name.markAsTouched();
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.name_field_required,
      );
    }

    if (this.contactForm.controls.phone.invalid) {
      this.contactForm.controls.phone.markAsTouched();
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.phone_field_required,
      );
    }

    if (this.contactForm.controls.email.invalid) {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.email_not_expected,
      );
      this.isValidationInvalid = true;
    }

    if (!!phone && phone.length < 12) {
      this.contactForm.controls['phone'].setErrors({
        incorrect: true,
      });
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.number_invalid,
      );
      this.isValidationInvalid = true;
    }

    if (this.contactForm.invalid || this.isValidationInvalid) return;

    const addressValidation = (add: any) => {
      if (Array.isArray(add)) return add.filter(Boolean).map((v) => v.trim());
      else if (add) return [{ address: add.address }];
      else return [];
    };

    const contactValidation = (el: any) => {
      if (Array.isArray(el)) return el.filter(Boolean);
      else if (el) return [el];
      else return [];
    };

    if (!this.contact) {
      this.isLoader = true;
      this.contactService
        .create({
          name: name.trim(),
          phone: contactValidation(phone),
          email: contactValidation(email),
          address: addressValidation(address),
          comment: comment,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.close.emit('close');
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_created,
            );
            this.isLoader = false;
          },
          error: (error) => {
            this.isLoader = false;
            (error.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code]);
            });

            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.contact_creation_error,
            );
          },
        });
    } else {
      this.isLoader = true;
      this.contactService
        .update(this.contact.id, {
          name: name.trim(),
          phone: contactValidation(phone),
          email: contactValidation(email),
          address: addressValidation(address),
          comment: comment.trim(),
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isLoader = false;
            this.close.emit('close');
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_updated,
            );
          },
          error: (e) => {
            this.isLoader = false;
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.contact_updating_error,
            );
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
            });
          },
        });
    }
  }

  public onDeleteContact(): void {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.contacts',
    });

    dialogRef.subscribe({
      next: (result: any) => {
        if (result) {
          this.contactService
            .delete(this.contact.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.close.emit('close');
                this.notificationService.showSuccess(
                  '',
                  activeTranslations.notifications_texts.success.successfully_deleted,
                );
              },
              error: (e) => {
                this.notificationService.showError(
                  '',
                  activeTranslations.notifications_texts.error.contact_deleting_error,
                );
                (e.error?.errors || []).forEach((err: IError) => {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                });
              },
            });
        }
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.something_went_wrong_1,
        );
      },
    });
  }

  public onCloseSidebar() {
    this.close.emit('close');
  }

  private initContact() {
    if (!this.contact_id) {
      this.initForm();
      this.isLoader = false;
      return;
    }
    this.contactService.getById(this.contact_id).subscribe({
      next: (res) => {
        this.contact = res;
        this.initForm();
        this.isLoader = false;
      },
      error: (e) => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });
      },
    });
  }

  public onNoClick(): void {
    this.close.emit('close');
  }
}
